import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TourV2Operator from './TourV2Operator'
import ItemTypeAndBookedLabel from '../Common/ItemTypeAndBookedLabel'
import StartAndEndLocation from '../Common/StartAndEndLocation'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import offerPageURL from 'lib/offer/offerPageURL'
import BookingsDetails from 'tripPlanner/components/TripModal/TripItems/Summary/Common/BookingsDetails'
import CheckInAndOutDate from 'tripPlanner/components/TripModal/TripItems/Summary/Common/CheckInAndOutDate'
import { useOrder } from 'tripPlanner/hooks/api/order'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import type { TourItem } from 'tripPlanner/types/tripItem'
import { getOrderId } from 'tripPlanner/utils'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'

interface Props {
  offer: App.Tours.TourV2Offer
  item: TourItem
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: 0 0 ${rem(12)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
`

function TourV2SummaryContent({ item, offer }: Props) {
  const intl = useIntl()
  const showDates =
    item.endDate || item.startDate || item.startDay || item.endDay

  const { cheapestVariation } = useCheapestOfTourV2Offer(offer)
  const image = cheapestVariation?.images[0]
  const IMAGE_PARAMS: ImageParams = { mobileAspectRatio: '16:8' }

  const orderId = getOrderId(item)
  const { data: order, isLoading } = useOrder(orderId)
  const offerUrl = offerPageURL(offer)

  const { setImmersiveMode } = useImmersiveModeHandlers()

  const setTripId = useCallback(() => {
    setImmersiveMode({ tripId: item.tripId, sectionId: tripItemSelectionId(item.id) })
  }, [setImmersiveMode, item])

  return (
    <>
      {image && (
        <ModalImageThumbnail imageParams={IMAGE_PARAMS} image={image} />
      )}
      <ModalContent>
        <VerticalSpacer gap={20}>
          {isLoading && <LoadingIndicator inline />}
          {!isLoading && (
            <>
              <ItemTypeAndBookedLabel item={item} />
              <Header>
                <div>
                  <ProductTypeLabel productType={offer.productType} />
                </div>
                <TourV2Operator brand={offer.brand} />
                {offerUrl && (
                  <TextLink weight="bold" to={offerUrl} onClick={setTripId}>
                    {intl.formatMessage({
                      id: 'ECkzr7hr3BTnByHgrVHO',
                      defaultMessage: 'View offer details',
                      description: 'Link text to view offer details in tour booking summary',
                    })}
                  </TextLink>
                )}
              </Header>
              {order && (
                <>
                  <BookingsDetails order={order} orderItemType="tour" />
                  {order.tourItems.map((tourItem) => {
                    return tourItem.tour.travellers.map((traveller, index) => (
                      <div key={`tourItems-traveller-${index}`}>
                        <BodyText variant="medium" weight="bold">
                          {intl.formatMessage(
                            {
                              id: 'bnPWv9qdmKpRONRu8lfP',
                              defaultMessage: 'Traveller {travellerNumber}',
                              description: 'Label for individual traveller in tour booking summary',
                            },
                            { travellerNumber: index + 1 },
                          )}
                        </BodyText>
                        <BodyText variant="large">
                          {intl.formatMessage(
                            {
                              id: '8k5o3qhiB-amL-Xm3jEZ',
                              defaultMessage: '{firstName} {lastName}',
                              description: 'Traveller full name in tour booking summary on the tour page',
                            },
                            {
                              firstName: traveller.firstName,
                              lastName: traveller.lastName,
                            },
                          )}
                        </BodyText>
                      </div>
                    ))
                  })}
                </>
              )}
              {showDates && (
                <CheckInAndOutDate
                  isAccommodation={false}
                  startDate={item.startDate}
                  endDate={item.endDate}
                  startTime={item.startTime}
                  endTime={item.endTime}
                />
              )}
              <StartAndEndLocation
                startPlace={item.startPlace}
                endPlace={item.endPlace}
              />
            </>
          )}
        </VerticalSpacer>
      </ModalContent>
    </>
  )
}

export default TourV2SummaryContent
